import { observer } from 'mobx-react-lite'
import {
  AddDependentButton,
  DependentProfileCardListSection,
  DependentProfileCardListSectionSkeleton,
  ExistingDependentItem,
  ProvidedDependentProfileCardListItem,
  ProvidedDependentProfileCardListEmptyState,
} from './index'
import type { DependentsInfoRequestedSupportingItemViewModel } from '@/features/jobs/details/tax-type/info-gathering/dependents-info/DependentsInfoRequestedSupportingItemViewModel'
import { formatFullName } from '@/utils/full-name-util'

export interface DependentProfileCardListSectionContainerProps {
  vm: DependentsInfoRequestedSupportingItemViewModel
  isDisabled?: boolean
}

/**
 * Container for the dependent profile section that wires up the view model.
 */
export const DependentProfileCardListSectionContainer = observer(
  function ProvidedDependentProfileSectionContainer({
    vm,
    isDisabled,
  }: DependentProfileCardListSectionContainerProps) {
    if (vm.isFetchingEditorData) {
      return <DependentProfileCardListSectionSkeleton />
    }

    const addButton = !vm.isReadOnly && (
      <AddDependentButtonContainer vm={vm} isDisabled={isDisabled} />
    )

    return (
      <DependentProfileCardListSection
        testId="dependents-list-section"
        action={vm.providedDependents.length > 0 ? addButton : null}
        renderEmptyState={() => (
          <ProvidedDependentProfileCardListEmptyState
            action={addButton}
            testId="dependents-list-empty-state"
          />
        )}
      >
        {vm.providedDependents.map((dependent) => {
          const fullName = formatFullName(
            dependent.personalInfo.firstName,
            dependent.personalInfo.middleInitial,
            dependent.personalInfo.lastName,
          )
          return (
            <ProvidedDependentProfileCardListItem
              key={dependent.id}
              testId={`dependent-card-${dependent.id}`}
              fullName={fullName}
              relationship={dependent.relationship}
              onAction={() => vm.showEditDependentDialog(dependent)}
              isReadOnly={vm.isReadOnly}
              isDisabled={isDisabled}
              onRemovePress={
                vm.isReadOnly
                  ? undefined
                  : () => vm.showRemoveDependentDialog(dependent)
              }
            />
          )
        })}
      </DependentProfileCardListSection>
    )
  },
)

interface AddDependentButtonContainerProps {
  vm: DependentsInfoRequestedSupportingItemViewModel
  isDisabled?: boolean
}

/**
 * Container for the AddDependentButton that wires up the view model.
 */
const AddDependentButtonContainer = observer(
  function AddDependentButtonContainer({
    vm,
    isDisabled,
  }: AddDependentButtonContainerProps) {
    return (
      <AddDependentButton
        testId="add-dependent-button"
        onAddNewPress={vm.showCreateDependentDialog}
        isDisabled={isDisabled}
      >
        {vm.existingAvailableDependents.map((dependent) => {
          const fullName = formatFullName(
            dependent.personalInfo.firstName,
            dependent.personalInfo.middleInitial,
            dependent.personalInfo.lastName,
          )
          return (
            <ExistingDependentItem
              key={dependent.id}
              fullName={fullName}
              relationship={dependent.relationship}
              onAction={() => vm.addExistingDependent(dependent)}
            />
          )
        })}
      </AddDependentButton>
    )
  },
)
